import React, { useMemo, useState } from 'react';
import { Card, Spin, Empty, Radio, Typography } from 'antd';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from 'recharts';

const { Text } = Typography;

const KPI_OPTIONS = [
  { value: 'receita', label: 'Receita', kind: 'currency' },
  { value: 'despesa', label: 'Despesa', kind: 'currency' },
  { value: 'saldo', label: 'Saldo', kind: 'currency' },
  { value: 'alunos', label: 'Alunos', kind: 'integer' },
  { value: 'matriculas', label: 'Matrículas', kind: 'integer' },
];

const MONTH_NAME_TO_NUMBER = {
  janeiro: 1,
  fevereiro: 2,
  marco: 3,
  abril: 4,
  maio: 5,
  junho: 6,
  julho: 7,
  agosto: 8,
  setembro: 9,
  outubro: 10,
  novembro: 11,
  dezembro: 12,
};

const MONTH_SHORT_PT = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatCurrencyBRL = (value) => (
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  }).format(toNumber(value))
);

const formatInteger = (value) => (
  new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 }).format(toNumber(value))
);

const sortByCompetencia = (a, b) => String(a?.competencia || '').localeCompare(String(b?.competencia || ''));

const normalizeMonthName = (value) => (
  String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
);

const normalizeFinanceiroHistorico = (historico) => {
  return (Array.isArray(historico) ? historico : []).map((item, index) => {
    const competenciaRaw = String(item?.competencia || '').slice(0, 7);
    const ano = toNumber(item?.ano);
    const mesNumero = toNumber(item?.mes);
    const mesNomeNormalizado = normalizeMonthName(item?.mes);

    let mes = 0;
    if (mesNumero >= 1 && mesNumero <= 12) {
      mes = mesNumero;
    } else if (MONTH_NAME_TO_NUMBER[mesNomeNormalizado]) {
      mes = MONTH_NAME_TO_NUMBER[mesNomeNormalizado];
    }

    const competenciaKey = competenciaRaw || (ano > 0 && mes > 0 ? `${ano}-${String(mes).padStart(2, '0')}` : '');
    const competenciaLabel = competenciaKey || `${item?.mes || 'Mês'} ${item?.ano || index + 1}`;

    return {
      competencia: competenciaLabel,
      competencia_key: competenciaKey,
      receita: toNumber(item?.receita ?? item?.receitas),
      despesa: toNumber(item?.despesa ?? item?.despesas),
      saldo: toNumber(item?.saldo),
    };
  });
};

const formatCompetenciaMesAno = (value) => {
  const input = String(value || '').trim();
  const match = input.match(/^(\d{4})-(\d{2})$/);

  if (match) {
    const ano = Number(match[1]);
    const mes = Number(match[2]);
    if (ano > 0 && mes >= 1 && mes <= 12) {
      return `${MONTH_SHORT_PT[mes - 1]}/${String(ano).slice(-2)}`;
    }
  }

  return input || '-';
};

const KPIHistoricoExplorerV2 = ({
  financeiroData,
  alunosHistorico,
  matriculasHistorico,
  forecastData,
  loading,
}) => {
  const [kpiSelecionado, setKpiSelecionado] = useState('receita');

  const financeiroHistorico = Array.isArray(financeiroData?.historico_12_meses)
    ? financeiroData.historico_12_meses
    : [];
  const alunosSerie = Array.isArray(alunosHistorico) ? alunosHistorico : [];
  const matriculasSerie = Array.isArray(matriculasHistorico) ? matriculasHistorico : [];
  const forecastSerie = Array.isArray(forecastData?.historico_competencia)
    ? forecastData.historico_competencia
    : [];
  const financeiroNormalizado = useMemo(
    () => normalizeFinanceiroHistorico(financeiroHistorico),
    [financeiroHistorico]
  );

  const kpiInfo = KPI_OPTIONS.find((option) => option.value === kpiSelecionado) || KPI_OPTIONS[0];

  const { chartData, mostrarPrevisto } = useMemo(() => {
    if (kpiSelecionado === 'receita') {
      const realUltimosSeis = financeiroNormalizado
        .slice(-6)
        .map((item) => ({
          competencia: item.competencia,
          competencia_key: item.competencia_key,
          valor_real: item.receita,
          valor_previsto: null,
        }));

      const chavesReais = new Set(realUltimosSeis.map((item) => item.competencia_key).filter(Boolean));
      const ultimaCompetenciaReal = realUltimosSeis
        .map((item) => item.competencia_key)
        .filter(Boolean)
        .sort()[realUltimosSeis.filter((item) => item.competencia_key).length - 1] || '';

      const previstosFuturos = forecastSerie
        .map((item) => {
          const competencia = String(item?.competencia || '').slice(0, 7);
          return {
            competencia,
            competencia_key: competencia,
            valor_previsto: toNumber(item?.previsto),
          };
        })
        .filter((item) => item.competencia_key)
        .filter((item) => (!ultimaCompetenciaReal || item.competencia_key > ultimaCompetenciaReal))
        .filter((item) => !chavesReais.has(item.competencia_key))
        .sort((a, b) => a.competencia_key.localeCompare(b.competencia_key))
        .slice(0, 3)
        .map((item) => ({
          competencia: item.competencia,
          competencia_key: item.competencia_key,
          valor_real: null,
          valor_previsto: item.valor_previsto,
        }));

      const data = [...realUltimosSeis, ...previstosFuturos];
      const possuiPrevisto = previstosFuturos.length > 0;

      return {
        chartData: data,
        mostrarPrevisto: possuiPrevisto,
      };
    }

    if (kpiSelecionado === 'despesa') {
      return {
        chartData: financeiroNormalizado
          .map((item) => ({ competencia: item.competencia, valor_real: item.despesa }))
          .filter((item) => item.competencia),
        mostrarPrevisto: false,
      };
    }

    if (kpiSelecionado === 'saldo') {
      return {
        chartData: financeiroNormalizado
          .map((item) => ({ competencia: item.competencia, valor_real: item.saldo }))
          .filter((item) => item.competencia),
        mostrarPrevisto: false,
      };
    }

    if (kpiSelecionado === 'alunos') {
      return {
        chartData: alunosSerie
          .map((item) => ({ competencia: item?.competencia, valor_real: toNumber(item?.total_alunos) }))
          .filter((item) => item.competencia)
          .sort(sortByCompetencia),
        mostrarPrevisto: false,
      };
    }

    return {
      chartData: matriculasSerie
        .map((item) => ({ competencia: item?.competencia, valor_real: toNumber(item?.total_matriculas) }))
        .filter((item) => item.competencia)
        .sort(sortByCompetencia),
      mostrarPrevisto: false,
    };
  }, [kpiSelecionado, financeiroNormalizado, alunosSerie, matriculasSerie, forecastSerie]);

  const formatValue = (value) => (kpiInfo.kind === 'currency' ? formatCurrencyBRL(value) : formatInteger(value));
  const resumoMensal = chartData.map((item, index) => ({
    key: `${item?.competencia || 'comp'}-${index}`,
    competencia: formatCompetenciaMesAno(item?.competencia),
    real: item?.valor_real,
    previsto: item?.valor_previsto,
  }));

  if (loading) {
    return (
      <Card title="Explorador Histórico de KPIs" style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
          <Spin size="large" tip="Carregando histórico de KPIs..." />
        </div>
      </Card>
    );
  }

  const temAlgumDado =
    financeiroHistorico.length > 0 ||
    alunosSerie.length > 0 ||
    matriculasSerie.length > 0 ||
    forecastSerie.length > 0;

  if (!temAlgumDado) {
    return (
      <Card title="Explorador Histórico de KPIs" style={{ marginBottom: '24px' }}>
        <Empty description="Sem dados históricos para exibir" />
      </Card>
    );
  }

  return (
    <Card title="Explorador Histórico de KPIs" style={{ marginBottom: '24px' }}>
      <div style={{ display: 'grid', gap: 16 }}>
        <Text type="secondary">KPI selecionado: {kpiInfo.label}</Text>

        <Radio.Group
          value={kpiSelecionado}
          onChange={(event) => setKpiSelecionado(event.target.value)}
          optionType="button"
          buttonStyle="solid"
        >
          {KPI_OPTIONS.map((item) => (
            <Radio.Button key={item.value} value={item.value}>
              {item.label}
            </Radio.Button>
          ))}
        </Radio.Group>

        {!chartData.length ? (
          <Empty description={`Sem histórico disponível para ${kpiInfo.label}`} />
        ) : (
          <div style={{ display: 'grid', gap: 12 }}>
            <div style={{ width: '100%', height: 360 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="competencia" tickFormatter={formatCompetenciaMesAno} />
                  <YAxis tickFormatter={formatValue} />
                  <Tooltip
                    labelFormatter={(label) => formatCompetenciaMesAno(label)}
                    formatter={(value, name) => [formatValue(value), name === 'valor_previsto' ? 'Previsto' : 'Real']}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="valor_real"
                    name="Real"
                    stroke="#1890ff"
                    strokeWidth={2}
                    dot={{ r: 2 }}
                    activeDot={{ r: 5 }}
                    connectNulls={false}
                  />
                  {kpiSelecionado === 'receita' && mostrarPrevisto && (
                    <Line
                      type="monotone"
                      dataKey="valor_previsto"
                      name="Previsto"
                      stroke="#faad14"
                      strokeWidth={2}
                      strokeDasharray="6 4"
                      dot={{ r: 2 }}
                      activeDot={{ r: 5 }}
                      connectNulls={false}
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {resumoMensal.map((item) => (
                <div
                  key={item.key}
                  style={{
                    border: '1px solid #f0f0f0',
                    borderRadius: 8,
                    padding: '6px 10px',
                    background: '#fafafa',
                  }}
                >
                  <Text style={{ display: 'block', fontSize: 12, color: '#8c8c8c' }}>{item.competencia}</Text>
                  <Text style={{ display: 'block', fontSize: 12 }}>
                    Real: {item.real === null || item.real === undefined ? '-' : formatValue(item.real)}
                  </Text>
                  {kpiSelecionado === 'receita' && mostrarPrevisto && (
                    <Text style={{ display: 'block', fontSize: 12 }}>
                      Prev.: {item.previsto === null || item.previsto === undefined ? '-' : formatValue(item.previsto)}
                    </Text>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

export default KPIHistoricoExplorerV2;
